// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { isNil } from 'lodash-es';

import { getErrorMessage } from '@/utils/errors';

import { RootState } from '../index';

const DEBUG = false;
const apiUrl = window.config.userApiUrl + '/auth/sessions';

type GenericError = {
  rejectValue: {
    error: string;
  };
};

const getAuthHeaders = (state: RootState): Record<string, string> | null => {
  const authToken = state.authentication.access_token;
  if (isNil(authToken)) return null;
  return { Authorization: authToken };
};

/* ---------- API response shapes ---------- */

/**
 * Best-effort metadata the server records against a session at sign-in time.
 * Present (`{ ip, user_agent }`) only on recently-created sessions; older ones
 * carry `null` or `{}`, so every field here is optional.
 */
export interface UserSessionTokenData {
  ip?: string;
  user_agent?: string;
  [key: string]: unknown;
}

/**
 * One active auth session ("logged-in device"), as returned by
 * `GET /auth/sessions`. The endpoint yields a bare array of these; `token` is
 * the handle the logout endpoint takes. The device/geo metadata (IP + user
 * agent) lives under `token_data`, which is `null`/`{}` for older sessions —
 * so callers must treat it as optional. The reducer also tolerates a
 * `{ data: [...], total }` envelope for forward-compatibility.
 */
export interface UserSession {
  token: string;
  user_id?: string;
  /** Absolute ISO 8601 UTC timestamp, millisecond precision — e.g. `"2026-06-16T13:38:05.299Z"`. */
  created_at?: string;
  /**
   * Absolute ISO 8601 UTC timestamp when this session expires (NOT a relative
   * TTL) — e.g. `"2026-06-23T13:38:05.298Z"`. Compare directly against
   * `Date.now()` via `Date.parse`; see `getAccessTokenRemainingMs`
   * (`@/utils/auth-token`), which reads the auth token's sibling `expires`
   * field under this same convention.
   */
  expires_at?: string;
  /** Sign-in metadata (IP, user agent); `null`/`{}` on older sessions. */
  token_data?: UserSessionTokenData | null;
  /** Whether this is the session the current request authenticated with. */
  current?: boolean;
  [key: string]: unknown;
}

/* ---------- thunks ----------

Endpoints (all GET, all require an `Authorization` header carrying the access token):
  GET {userApiUrl}/auth/sessions                 → list active sessions
  GET {userApiUrl}/auth/sessions/logout?token=X  → revoke ONE session by token
  GET {userApiUrl}/auth/sessions/logout-all      → revoke ALL sessions for the user

NOTE: these live under `/auth/`, which the global 401 interceptor
(`utils/auth-interceptor`) deliberately excludes from its auto-logout/redirect —
so errors here surface locally in this slice instead of bouncing the user to the
register funnel. After `logoutAll` (or revoking the CURRENT session's token) you
should also dispatch the local `logout()` action from the authentication slice to
clear this browser's stored tokens — the server revoke does not touch localStorage.
*/

/** Normalise the list response into a flat `UserSession[]`. */
const unwrapSessions = (payload: any): { sessions: UserSession[]; total: number } => {
  if (isNil(payload)) return { sessions: [], total: 0 };
  if (Array.isArray(payload)) return { sessions: payload as UserSession[], total: payload.length };
  if (Array.isArray(payload.data)) {
    return { sessions: payload.data as UserSession[], total: payload.total ?? payload.data.length };
  }
  if (Array.isArray(payload.sessions)) {
    return { sessions: payload.sessions as UserSession[], total: payload.total ?? payload.sessions.length };
  }
  return { sessions: [], total: 0 };
};

export const fetchUserSessions = createAsyncThunk<{ sessions: UserSession[]; total: number }, void, GenericError>(
  'userSessions/fetch',
  async (_, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      const response = await axios.get(apiUrl, { headers });
      // Log only metadata — the full response carries the Authorization header.
      DEBUG && console.log('[UserSessions Slice] fetch status', response.status);

      if (!isNil(response.data)) return unwrapSessions(response.data);
      return rejectWithValue({ error: "Couldn't fetch user sessions" });
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/** Revoke a single session by its token. Returns the revoked token so the reducer
 *  can drop it from the list. */
export const logoutUserSession = createAsyncThunk<{ token: string }, { token: string }, GenericError>(
  'userSessions/logout',
  async ({ token }, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });
      if (isNil(token)) return rejectWithValue({ error: 'token is required' });

      const response = await axios.get(`${apiUrl}/logout`, { headers, params: { token } });
      DEBUG && console.log('[UserSessions Slice] logout status', response.status);

      return { token };
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/** Revoke ALL of the user's sessions (including the current one). The caller
 *  should follow a successful dispatch with the authentication slice's `logout()`
 *  to clear this browser's stored tokens. */
export const logoutAllUserSessions = createAsyncThunk<void, void, GenericError>(
  'userSessions/logoutAll',
  async (_, { rejectWithValue, getState }) => {
    try {
      const headers = getAuthHeaders(getState() as RootState);
      if (!headers) return rejectWithValue({ error: 'No authentication token found' });

      const response = await axios.get(`${apiUrl}/logout-all`, { headers });
      DEBUG && console.log('[UserSessions Slice] logout-all status', response.status);

      return undefined;
    } catch (error: any) {
      return rejectWithValue({ error: getErrorMessage(error) });
    }
  },
);

/* ---------- slice state ---------- */

export interface UserSessionsState {
  sessions: UserSession[];
  total: number;

  /** GET /auth/sessions request status. */
  loading: boolean;
  loaded: boolean;
  error: string | null | undefined;

  /** Token currently being revoked (for per-row spinners), or `null`. */
  revokingToken: string | null;
  revokeError: string | null | undefined;

  /** logout-all request status. */
  revokingAll: boolean;
  revokeAllError: string | null | undefined;
}

const initialState: UserSessionsState = {
  sessions: [],
  total: 0,

  loading: false,
  loaded: false,
  error: null,

  revokingToken: null,
  revokeError: null,

  revokingAll: false,
  revokeAllError: null,
};

export const userSessionsSlice = createSlice({
  name: 'userSessions',
  initialState,
  reducers: {
    /**
     * Drop the cached session list back to empty/unloaded. Dispatched by the
     * central logout + identity-change teardowns (`store/index.ts`): the list is
     * identity-scoped (the owner's own active logins, fetched one-shot behind a
     * `!loaded` guard) and the revoke actions target those tokens, so it must NOT
     * outlive the session — otherwise the next/impersonated owner on the same
     * browser sees, and can revoke, the PREVIOUS owner's sessions.
     */
    resetUserSessions: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      /* ----- GET /auth/sessions ----- */
      .addCase(fetchUserSessions.pending, (state) => {
        state.loading = true;
        state.loaded = false;
        state.error = null;
      })
      .addCase(fetchUserSessions.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.sessions = action.payload.sessions;
        state.total = action.payload.total;
      })
      .addCase(fetchUserSessions.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.error = action.payload?.error;
      })

      /* ----- GET /auth/sessions/logout ----- */
      .addCase(logoutUserSession.pending, (state, action) => {
        state.revokingToken = action.meta.arg.token;
        state.revokeError = null;
      })
      .addCase(logoutUserSession.fulfilled, (state, action) => {
        state.revokingToken = null;
        state.sessions = state.sessions.filter((s) => s.token !== action.payload.token);
        state.total = state.sessions.length;
      })
      .addCase(logoutUserSession.rejected, (state, action) => {
        state.revokingToken = null;
        state.revokeError = action.payload?.error;
      })

      /* ----- GET /auth/sessions/logout-all ----- */
      .addCase(logoutAllUserSessions.pending, (state) => {
        state.revokingAll = true;
        state.revokeAllError = null;
      })
      .addCase(logoutAllUserSessions.fulfilled, (state) => {
        state.revokingAll = false;
        state.sessions = [];
        state.total = 0;
      })
      .addCase(logoutAllUserSessions.rejected, (state, action) => {
        state.revokingAll = false;
        state.revokeAllError = action.payload?.error;
      });
  },
});

export const { resetUserSessions } = userSessionsSlice.actions;

/* ---------- selectors ---------- */

export const selectUserSessions = (state: RootState): UserSession[] => state.userSessions.sessions;

export default userSessionsSlice.reducer;
